import type { SVGProps } from 'react';

/** Single stroke weight and cap style across the whole set, so the icons read
 *  as one family against the soft surfaces. */
function Icon({ children, size = 24, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const PlayIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M8 5.6a.6.6 0 0 1 .92-.5l9 6.4a.6.6 0 0 1 0 1l-9 6.4a.6.6 0 0 1-.92-.5Z" fill="currentColor" stroke="none" />
  </Icon>
);

export const PauseIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <rect x="7" y="5" width="3.6" height="14" rx="1.4" fill="currentColor" stroke="none" />
    <rect x="13.4" y="5" width="3.6" height="14" rx="1.4" fill="currentColor" stroke="none" />
  </Icon>
);

export const PrevIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M18 6.5v11a.5.5 0 0 1-.79.4L9.5 12l7.71-5.9a.5.5 0 0 1 .79.4Z" fill="currentColor" stroke="none" />
    <path d="M6.5 5.5v13" />
  </Icon>
);

export const NextIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M6 6.5v11a.5.5 0 0 0 .79.4L14.5 12 6.79 6.1A.5.5 0 0 0 6 6.5Z" fill="currentColor" stroke="none" />
    <path d="M17.5 5.5v13" />
  </Icon>
);

/** Circular-arrow rewind/forward marks with the interval baked in, the way
 *  every audiobook app draws them. */
export const SkipBackIcon = ({
  seconds = 15,
  ...p
}: SVGProps<SVGSVGElement> & { size?: number; seconds?: number }) => (
  <Icon {...p}>
    <path d="M12 5a7 7 0 1 1-6.6 4.7" />
    <path d="M4.4 4.6v4.6h4.6" />
    <text
      x="12"
      y="15.6"
      textAnchor="middle"
      fontSize="7.4"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      fontFamily="inherit"
    >
      {seconds}
    </text>
  </Icon>
);

export const SkipForwardIcon = ({
  seconds = 30,
  ...p
}: SVGProps<SVGSVGElement> & { size?: number; seconds?: number }) => (
  <Icon {...p}>
    <path d="M12 5a7 7 0 1 0 6.6 4.7" />
    <path d="M19.6 4.6v4.6H15" />
    <text
      x="12"
      y="15.6"
      textAnchor="middle"
      fontSize="7.4"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      fontFamily="inherit"
    >
      {seconds}
    </text>
  </Icon>
);

export const ListIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />
  </Icon>
);

export const MoonIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2Z" />
  </Icon>
);

export const SunIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.6v2.2M12 19.2v2.2M4.2 12H2M22 12h-2.2M6.3 6.3 4.8 4.8M19.2 19.2l-1.5-1.5M17.7 6.3l1.5-1.5M4.8 19.2l1.5-1.5" />
  </Icon>
);

export const SpeedIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M4.6 17.5a8.5 8.5 0 1 1 14.8 0" />
    <path d="M12 12.8 15.8 9" />
    <circle cx="12" cy="13.6" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
);

export const CogIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3.1" />
    <path d="M19.4 14.6a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.11a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.11a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.11a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.11a1.7 1.7 0 0 0-1.49 1.03Z" />
  </Icon>
);

export const BackIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </Icon>
);

export const CheckIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M4.8 12.6 9.6 17.4 19.2 6.6" />
  </Icon>
);

export const RefreshIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M20 11.4a8 8 0 1 0-.72 4.6" />
    <path d="M20 4.6v6h-6" />
  </Icon>
);

export const BookIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M5 4.8A1.8 1.8 0 0 1 6.8 3H19v15.4H6.8A1.8 1.8 0 0 0 5 20.2Z" />
    <path d="M5 18.4A1.8 1.8 0 0 1 6.8 20.6H19" />
  </Icon>
);

export const FolderIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M3.5 7.2a1.7 1.7 0 0 1 1.7-1.7h4.1l2 2.2h7.5a1.7 1.7 0 0 1 1.7 1.7v8.4a1.7 1.7 0 0 1-1.7 1.7H5.2a1.7 1.7 0 0 1-1.7-1.7Z" />
  </Icon>
);

export const SearchIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <circle cx="10.8" cy="10.8" r="6.3" />
    <path d="m15.5 15.5 4.4 4.4" />
  </Icon>
);

export const CloseIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </Icon>
);

export const CloudIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <path d="M7.2 18.5a4.2 4.2 0 0 1-.5-8.37 5.6 5.6 0 0 1 10.83-1.4A3.9 3.9 0 0 1 17.4 18.5Z" />
  </Icon>
);

export const AlertIcon = (p: SVGProps<SVGSVGElement> & { size?: number }) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.6" />
    <path d="M12 7.8v4.6M12 16.1h.01" />
  </Icon>
);

/**
 * The app's mark: book spines standing on a shelf, which also read as the
 * bars of a level meter — books you listen to. The tallest is lit in lamp
 * amber, and the last leans on its neighbour. The same drawing is the favicon, the home-screen icon
 * (scripts/make-icons.mjs) and the launch screen (index.html).
 */
const SPINES = [
  { x: 137, y: 202, h: 150, fill: '#eef1f8', opacity: 1, tilt: 0 },
  { x: 201, y: 138, h: 214, fill: '#f3b661', opacity: 1, tilt: 0 },
  { x: 265, y: 232, h: 120, fill: '#eef1f8', opacity: 1, tilt: 0 },
  { x: 329, y: 170, h: 182, fill: '#eef1f8', opacity: 0.86, tilt: -9 },
];

export const ShelfMark = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 512 512" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="shelf-tile" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1c2d5c" />
        <stop offset="1" stopColor="#2a1d57" />
      </linearGradient>
      <radialGradient id="shelf-lamp" cx="0.74" cy="0.2" r="0.62">
        <stop offset="0" stopColor="#f3b661" stopOpacity="0.5" />
        <stop offset="1" stopColor="#f3b661" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="512" height="512" rx="116" fill="url(#shelf-tile)" />
    <rect width="512" height="512" rx="116" fill="url(#shelf-lamp)" />
    {SPINES.map((sp) => (
      <g
        key={sp.x}
        transform={sp.tilt ? `rotate(${sp.tilt} ${sp.x} ${sp.y + sp.h})` : undefined}
      >
        <rect x={sp.x} y={sp.y} width="46" height={sp.h} rx="12" fill={sp.fill} opacity={sp.opacity} />
        <rect x={sp.x} y={sp.y + 22} width="46" height="9" fill="#101b33" opacity="0.22" />
        <rect x={sp.x} y={sp.y + sp.h - 34} width="46" height="9" fill="#101b33" opacity="0.22" />
      </g>
    ))}
    <rect x="96" y="358" width="320" height="18" rx="9" fill="#eef1f8" opacity="0.92" />
  </svg>
);
